import type { OnuPort, OnuVlan, WanCandidate } from '@simpleacs/shared';

/**
 * Which ports ride which WAN — ONE concept, two places to edit it (operator,
 * 2026-10-14: "there should only be one concept for bridge, and treat all lan
 * ports and wifi as ports, where I can add remove which port is associated to
 * which wan, interchangeable with lan ports tab").
 *
 * The record is the port row (`onu_ports`): the Ports tab edits it by type and
 * VLAN, the WAN card by connection, and both send the same PUT …/ports. A
 * bridge on VLAN V holds the ports whose untagged (or tagged) VLAN is V; the
 * routed subscriber WAN holds the ports on the router's own LAN (`lan`). A
 * WiFi port's place is the ROUTER's word (`network.bridgeVlan`), because a
 * network the router binds to a bridge is on it whatever the record says.
 *
 * Every port sits on one WAN: ticking it on a card moves it there. The LAN is
 * where a port goes when a bridge lets it go, so the routed card only takes
 * ports — it never drops one into nowhere.
 */
export type WanPortTarget = { kind: 'bridge'; vlanId: number } | { kind: 'lan' };

/** The WAN a connection's card assigns ports to, or null for one that takes none (management, an untagged bridge). */
export function wanPortTarget(c: WanCandidate): WanPortTarget | null {
  if (c.management || c.role === 'management') return null;
  if (c.role === 'bridge') {
    const v = Number(c.vlanId);
    return Number.isInteger(v) && v >= 1 && v <= 4094 ? { kind: 'bridge', vlanId: v } : null;
  }
  return c.role === 'internet' && c.primary ? { kind: 'lan' } : null;
}

/** Whether a port rides this WAN now. */
/**
 * The ports a WAN card offers: every socket, and the WiFi networks that are
 * ON. A network that is off rides no WAN and cannot be ticked onto one, so
 * listing it on the card only read as "still on the WAN" (operator,
 * 2026-10-16: "when i turn off ssids, why are they still showing on the wan
 * card"). The Ports tab still lists it, as "off — on no WAN"; switching it on
 * from the WiFi card is what brings it back here.
 */
export function portsOnOffer(ports: OnuPort[]): OnuPort[] {
  return ports.filter((p) => !(p.kind === 'wifi' && p.network?.on === false));
}

export function portOnWan(p: OnuPort, t: WanPortTarget): boolean {
  if (p.kind === 'wifi' && p.network) {
    // A network that is OFF rides no WAN (2026-10-14), whatever the router's binding leaf says.
    if (p.network.on === false) return false;
    return t.kind === 'bridge' ? p.network.bridgeVlan === t.vlanId : p.network.bridgeVlan === null;
  }
  if (!p.configured) return false;
  if (t.kind === 'lan') return p.mode === 'lan';
  return (p.mode === 'access' || p.mode === 'hybrid') && (p.pvid === t.vlanId || p.taggedVlans.includes(t.vlanId));
}

/**
 * The WAN a port hangs off, from what the router reports (operator,
 * 2026-10-14: "ports associate with the wan as a parent … not just associated
 * to a vlan without a wan"): a LAN-mode port rides the routed subscriber WAN,
 * a port on VLAN V rides the bridge on V. `missingVlan` names a VLAN the
 * record puts ports on while the router carries no WAN for it — the state
 * HS MC6 - Maggie3 was in.
 */
export function portParent(
  p: OnuPort,
  candidates: WanCandidate[],
): { label: string; missingVlan: number | null; off?: boolean } | null {
  // A network that is OFF rides no WAN, whatever the record or the router's binding says.
  if (p.kind === 'wifi' && p.network?.on === false) return { label: '', missingVlan: null, off: true };
  if (!p.configured) return null;
  if (p.mode === 'lan') {
    const routed = candidates.find((c) => c.role === 'internet' && c.primary);
    return routed ? { label: routed.label || 'the routed WAN', missingVlan: null } : null;
  }
  const vlan = p.mode === 'access' || p.mode === 'hybrid' ? p.pvid : null;
  if (vlan === null) return null;
  const bridge = candidates.find((c) => c.role === 'bridge' && Number(c.vlanId) === vlan);
  return bridge ? { label: bridge.label || `the VLAN ${vlan} bridge`, missingVlan: null } : { label: '', missingVlan: vlan };
}

/**
 * An Ethernet socket's link in a few words: "plugged in · 100M · full",
 * "nothing plugged in", or null when the router has not said (operator,
 * 2026-10-14: "show negotiation like 10M/100M/1G").
 */
export function portLinkLabel(link: OnuPort['link']): { up: boolean; text: string } | null {
  if (!link || link.up === null) return null;
  if (!link.up) return { up: false, text: 'empty' };
  const speed =
    link.speedMbps === null ? null : link.speedMbps >= 1000 ? `${Number((link.speedMbps / 1000).toFixed(1))}G` : `${link.speedMbps}M`;
  const words = ['plugged in', ...(speed ? [speed] : []), ...(link.duplex ? [link.duplex] : [])];
  return { up: true, text: words.join(' · ') };
}

/**
 * What a port is called everywhere: "LAN 2", "WiFi 1 · Mymanga Public WiFi"
 * (operator, 2026-10-14: "label the eth ports as lan 1, lan 2 etc and wifi
 * as wifi 1, wifi 2 etc") — from the socket's own number, never the vendor's
 * `eth_0/2`; an operator's own label is shown beside it, not instead of it.
 */
export function portName(p: OnuPort): string {
  const n = /\/(\d+)$/.exec(p.port)?.[1];
  if (p.kind !== 'wifi') return n ? `LAN ${n}` : p.label || p.port;
  const ssid = p.network?.ssid?.trim();
  return ssid ? `WiFi ${n ?? ''} · ${ssid}` : `WiFi ${n ?? ''}`;
}

/** Why the card cannot assign ports to this WAN, or null when it can. */
export function wanPortsBlocked(t: WanPortTarget, vlans: OnuVlan[]): string | null {
  if (t.kind === 'lan') return null;
  const held = vlans.filter((v) => v.vlanId === t.vlanId);
  if (held.length === 0) return `VLAN ${t.vlanId} is not attached to this ONU — attach it on the VLANs tab to give this bridge ports.`;
  if (!held.some((v) => v.mode === 'bridge')) {
    return `VLAN ${t.vlanId} is attached as routed — make it bridged on the VLANs tab to give this bridge ports.`;
  }
  return null;
}

/**
 * The ports a card's "+" may add to this WAN (operator, 2026-10-28: "do not
 * show ports not being used in a wan interface, instead, put a plus sign to
 * add an interface if it hasn't already been assigned to another interface"):
 * a port that is NOT on this WAN and is not held by another bridge — its home
 * is the router's own LAN (an unconfigured socket, a LAN-mode socket, a WiFi
 * network the router binds to no bridge). For the routed card that set is
 * empty: every port off the LAN is on some bridge, and leaves it only by being
 * let go there.
 */
export function portsAddable(ports: OnuPort[], t: WanPortTarget): OnuPort[] {
  if (t.kind === 'lan') return [];
  return ports.filter((p) => {
    if (portOnWan(p, t)) return false;
    if (p.kind === 'wifi') return !!p.network && p.network.on !== false && p.network.bridgeVlan === null;
    return !p.configured || p.mode === 'lan';
  });
}

/** The PUT …/ports body that puts a port on (or, off a bridge, back on the LAN). */
export function portPayload(p: OnuPort, t: WanPortTarget, on: boolean) {
  const toBridge = on && t.kind === 'bridge';
  return {
    port: p.port,
    kind: p.kind,
    label: p.label ?? (p.kind === 'wifi' ? `WiFi ${/\/(\d+)$/.exec(p.port)?.[1] ?? ''}` : null),
    mode: toBridge ? ('access' as const) : ('lan' as const),
    pvid: toBridge ? (t as { vlanId: number }).vlanId : null,
    taggedVlans: [] as number[],
    enabled: true,
    // The socket's DHCP choice is the Ports tab's; moving it between WANs keeps it.
    ...(p.kind === 'wifi' ? {} : { dhcp: p.dhcp ?? null }),
  };
}

/** The changes a card's draft makes, in the order they are sent: ports let go first, then ports taken. */
export function portChanges(ports: OnuPort[], t: WanPortTarget, chosen: ReadonlySet<string>) {
  const off = ports.filter((p) => portOnWan(p, t) && !chosen.has(p.port));
  const on = ports.filter((p) => !portOnWan(p, t) && chosen.has(p.port));
  return [...off.map((p) => ({ port: p, on: false })), ...on.map((p) => ({ port: p, on: true }))];
}

/**
 * Send a card's changes, one port at a time, through the same PUT the Ports
 * tab saves with. Each answer is gathered into one sentence; an OLT refusal
 * or a WiFi network the router would not take stops nothing that already
 * landed, and fails the task so the toast stays until it is read.
 */
export async function runWanPorts(
  base: string,
  t: WanPortTarget,
  changes: Array<{ port: OnuPort; on: boolean }>,
  api: { put: <T>(path: string, body?: unknown) => Promise<T> },
): Promise<{ message: string }> {
  const where = t.kind === 'bridge' ? `VLAN ${t.vlanId}` : 'the LAN';
  const notes: string[] = [];
  const problems: string[] = [];
  for (const { port, on } of changes) {
    const r = await api.put<{ applied?: { executed: boolean; errors: string[] } | null; router?: string[]; bound?: boolean | null }>(
      `${base}/ports`,
      portPayload(port, t, on),
    );
    const offNote = on && port.kind === 'wifi' && port.network?.on === false ? ' — it is switched off, so it rides nothing until it is switched on from the WiFi card' : '';
    notes.push(`${portName(port)} ${on ? `on ${where}` : 'back on the LAN'}${offNote}.`);
    notes.push(...(r.router ?? []));
    if (r.applied?.errors.length) problems.push(`the OLT refused ${portName(port)}: ${r.applied.errors[0]}`);
    if (on && port.kind === 'wifi' && r.bound === false) problems.push(`the router did not put ${portName(port)} on ${where}`);
  }
  const message = notes.join(' ');
  if (problems.length > 0) throw new Error(`${message} Not all of it landed — ${problems.join('; ')}. The record is kept.`);
  return { message };
}
